"use client";

import { SingleProduct } from "@/__generated__/graphql";
import { Box, Button, Stack, Typography, useMediaQuery, useScrollTrigger, useTheme } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import ProductMidSectionDescription from "./ProductMidSectionDescription";
import ProductMidSectionSpecifications from "./ProductMidSectionSpecifications";
import ProductFeaturesView from "./ProductFeaturesView";
import ProductMobileFeaturesStrip from "./ProductMobileFeaturesStrip";
import { useAppContext } from "@/app/(main)/_components/AppProvider";

type Props = {
  product: SingleProduct;
};

export default function ProductMidSection({ product }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const trigger = useScrollTrigger({});
  const { hasBanner, bannerOptions } = useAppContext();
  const navRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  const scrollLock = useRef(0);
  const [activeTab, setActiveTab] = useState(0);

  const list = useMemo(() => {
    const sections = [];
    const hasTags = product.tags?.some((tag) => tag?.title);

    if (product.description || hasTags) {
      sections.push({
        name: "description",
        title: "توضیحات",
        content: <ProductMidSectionDescription description={product.description} tags={product.tags} />,
      });
    }

    // On mobile: features strip above description; specs strip stays under the title.
    if (!isMobile && product.features?.length) {
      sections.push({
        name: "features",
        title: "ویژگی‌ها",
        content: <ProductFeaturesView features={product.features} />,
      });
    }

    if (!isMobile && product.specifications && product.specifications.length > 0) {
      sections.push({
        name: "specifications",
        title: "مشخصات",
        content: <ProductMidSectionSpecifications specifications={product.specifications} />,
      });
    }

    return sections;
  }, [isMobile, product.description, product.features, product.specifications, product.tags]);

  const anchorOffset = (scrollingDown: boolean) => {
    const header = document.querySelector("header");
    const nav = navRef.current;
    if (!nav) return 0;
    const headerHeight = header?.getBoundingClientRect().height ?? 0;
    const menu = header?.querySelector(".MuiCollapse-root");
    const menuHeight = menu?.getBoundingClientRect().height ?? 0;
    const menuFull = menu?.scrollHeight ?? 0;
    // Scrolling down collapses the header menu; scrolling up opens it.
    const bar = scrollingDown ? headerHeight - menuHeight : headerHeight - menuHeight + menuFull;
    return bar + nav.offsetHeight + 8;
  };

  useEffect(() => {
    const update = () => {
      if (scrollLock.current) return;
      const nav = navRef.current;
      if (!nav) return;
      const marker = nav.getBoundingClientRect().bottom + 16;
      let current = 0;
      sectionRefs.current.forEach((section, index) => {
        if (section && section.getBoundingClientRect().top <= marker) current = index;
      });
      setActiveTab((prev) => (prev === current ? prev : current));
    };

    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    const sections = sectionRefs.current.filter(Boolean) as HTMLElement[];
    const observer = new ResizeObserver(onScroll);
    sections.forEach((section) => observer.observe(section));

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [list.length]);

  const hasMobileFeatures = isMobile && Boolean(product.features?.length);

  if (!list.length && !hasMobileFeatures) {
    return null;
  }

  const handleTabClick = (index: number) => {
    const section = sectionRefs.current[index];
    if (!section) return;
    setActiveTab(index);
    const sectionY = section.getBoundingClientRect().top + window.scrollY;
    const top = Math.max(0, sectionY - anchorOffset(sectionY > window.scrollY + 2));
    const token = ++scrollLock.current;
    window.scrollTo({ top, behavior: "smooth" });
    const unlock = () => {
      if (scrollLock.current !== token) return;
      scrollLock.current = 0;
      window.dispatchEvent(new Event("scroll"));
    };
    const timer = window.setTimeout(unlock, 900);
    window.addEventListener(
      "scrollend",
      () => {
        window.clearTimeout(timer);
        unlock();
      },
      { once: true },
    );
  };
  
  const showTabs = list.length > 1;

  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      {isMobile && product.features?.length ? <ProductMobileFeaturesStrip features={product.features} /> : null}

      {list.length ? (
        <Box>
          {!showTabs ? (
            <Typography
              id={`product-heading-${list[0].name}`}
              component="h2"
              variant="subtitle2"
              sx={{ mb: 0.75, fontWeight: 600, color: "grey.800", display: { md: "none" } }}
            >
              {list[0].title}
            </Typography>
          ) : null}

          <Box
            sx={{
              bgcolor: "background.paper",
              borderRadius: 3,
              border: "1px solid",
              borderColor: "grey.100",
              minWidth: 0,
              overflowX: "clip",
            }}
          >
            {showTabs ? (
              <Stack
                ref={navRef}
                component="nav"
                aria-label="جزئیات محصول"
                data-tabs
                direction="row"
                sx={(theme) => ({
                  position: "sticky",
                  zIndex: 2,
                  background: theme.palette.background.paper,
                  borderRadius: "12px 12px 0 0",
                  borderBottom: `1px solid ${theme.palette.grey[100]}`,
                  top: trigger
                    ? {
                        xs: 44 + (hasBanner ? bannerOptions.sx.minHeight.xs : 0),
                        xsm: 48 + (hasBanner ? bannerOptions.sx.minHeight.xs : 0),
                        sm: 48 + (hasBanner ? bannerOptions.sx.minHeight.sm : 0),
                        md: 75 + (hasBanner ? bannerOptions.sx.minHeight.md : 0),
                      }
                    : {
                        xs: 44 + (hasBanner ? bannerOptions.sx.minHeight.xs : 0),
                        xsm: 48 + (hasBanner ? bannerOptions.sx.minHeight.xs : 0),
                        sm: 48 + (hasBanner ? bannerOptions.sx.minHeight.sm : 0),
                        md: 123 + (hasBanner ? bannerOptions.sx.minHeight.md : 0),
                      },
                })}
              >
                {list.map((item, index) => {
                  const isActive = activeTab === index;
                  return (
                    <Button
                      key={item.name}
                      id={`product-tab-${item.name}`}
                      aria-current={isActive ? "location" : undefined}
                      data-tab
                      variant="text"
                      color={isActive ? "secondary" : "inherit"}
                      onClick={() => handleTabClick(index)}
                      sx={(theme) => ({
                        borderRadius: 0,
                        minHeight: { xs: 48, md: 52 },
                        px: { xs: 2, md: 2.5 },
                        color: isActive ? "secondary.main" : "grey.700",
                        borderBottom: `2px solid ${isActive ? theme.palette.secondary.main : "transparent"}`,
                      })}
                    >
                      <Typography variant="subtitle1" component="span">
                        {item.title}
                      </Typography>
                    </Button>
                  );
                })}
              </Stack>
            ) : null}

            {list.map((item, index) => (
              <Box
                key={item.name}
                id={item.name}
                component="section"
                ref={(el: HTMLElement | null) => {
                  sectionRefs.current[index] = el;
                }}
                aria-labelledby={`product-heading-${item.name}`}
                data-tab-content
                sx={(theme) => ({
                  px: { xs: 1.5, sm: 2.5, md: 3 },
                  py: { xs: 2, md: 3 },
                  ...(index > 0 && { borderTop: `1px solid ${theme.palette.grey[100]}` }),
                })}
              >
                {showTabs ? (
                  <Typography id={`product-heading-${item.name}`} component="h2" variant="subtitle2" className="visually-hidden">
                    {item.title}
                  </Typography>
                ) : null}
                {item.content}
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}
    </Stack>
  );
}
